import * as tslib_1 from "tslib";
import React from 'react';
import omit from 'lodash/omit';
import { lowDeprecatedWarning } from "../utils";
/*
  deprecateProps helper.
  ===

  For removing props use syntax `deprecateProps(propsToRemove)(TargetComponent);`
  It prints warning in console if somebody passes deprecated prop.

  Example:
    You need to deprecate `async` prop in Select component:
    `deprecateProps(['async'])(Select);`

  ===

  For renaming props use syntax deprecateProps(propsToRemove, propsToRename)(TargetComponent);
  It prints warning if somebody passes old prop, and passes old value as a new prop.

  Example:
    You need to rename `async` prop to `useAsync` in Select component:
    `deprecateProps([], { async: 'useAsync' })(Select);`

    If you don't have propsToRemove (1 argument) - pass empty array.
*/

var deprecateProps = function deprecateProps(propsToRemove, propsToRename) {
  if (propsToRemove === void 0) {
    propsToRemove = [];
  }

  if (propsToRename === void 0) {
    propsToRename = {};
  }

  return function (Wrapped) {
    if (!(propsToRemove instanceof Array) || !(propsToRename instanceof Object)) {
      throw new Error("Bad arguments for deprecateProps enhancer: 1 argument should be array of \n      strings (removed deprecated props), 2 argument should be object\n      (for renaming and deprecation props - keys are old prop names,\n      values - new prop names)");
    } // displayName is for Wrapped as a class
    // name is for Wrapped as a function


    var displayName = Wrapped.displayName || Wrapped.name;

    var Wrapper =
    /** @class */
    function (_super) {
      tslib_1.__extends(Wrapper, _super);

      function Wrapper() {
        var _this = _super !== null && _super.apply(this, arguments) || this;

        _this.getRenamedProps = function (propsToRenameNames) {
          return propsToRenameNames.reduce(function (props, oldProp) {
            var renamedProps = props;
            if (!_this.props.hasOwnProperty(oldProp)) return renamedProps;
            var newPropName = propsToRename[oldProp];
            renamedProps[newPropName] = _this.props[oldProp];
            return renamedProps;
          }, {});
        };

        return _this;
      }

      Wrapper.prototype.componentDidMount = function () {
        this.validateProps({}, this.props);
      };

      Wrapper.prototype.componentWillReceiveProps = function (nextProps) {
        this.validateProps(this.props, nextProps);
      }; // eslint-disable-next-line class-methods-use-this


      Wrapper.prototype.validateProps = function (prevProps, nextProps) {
        var oldPropsToRename = Object.keys(propsToRename);

        tslib_1.__spread(propsToRemove, oldPropsToRename).forEach(function (deprecatedProp) {
          if (!prevProps.hasOwnProperty(deprecatedProp) && nextProps.hasOwnProperty(deprecatedProp)) {
            var message = propsToRename.hasOwnProperty(deprecatedProp) ? "(" + displayName + ") prop '" + deprecatedProp + "' was renamed to '" + propsToRename[deprecatedProp] + "'. Old prop will be removed in next major version" // Renaming prop
            : "(" + displayName + ") prop '" + deprecatedProp + "' is deprecated and will be removed in next major version"; // Removing prop

            lowDeprecatedWarning(message);
          }
        });
      };

      Wrapper.prototype.render = function () {
        var propsToRenameNames = Object.keys(propsToRename);
        var validProps = omit(this.props, propsToRenameNames);
        var renamedProps = this.getRenamedProps(propsToRenameNames);
        return React.createElement(Wrapped, tslib_1.__assign({}, validProps, renamedProps));
      };

      Wrapper.displayName = displayName;
      Wrapper.defaultProps = Wrapped.defaultProps;
      return Wrapper;
    }(React.Component); // extend propTypes with deprecated values


    var propsToRenameNames = Object.keys(propsToRename);

    if (typeof Wrapped.propTypes === 'object') {
      Wrapper.propTypes = tslib_1.__assign({}, Wrapped.propTypes);
      Object.keys(Wrapped.propTypes).forEach(function (prop) {
        var deprecatedName = propsToRenameNames.find(function (name) {
          return propsToRename[name] === prop;
        });

        if (deprecatedName) {
          Wrapper.propTypes[deprecatedName] = Wrapped.propTypes[prop];
        }
      });
    }

    return Wrapper;
  };
};

export default deprecateProps;